import React, {
  FunctionComponent, useEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';
import cloneDeep from 'lodash.clonedeep';
import { RootState } from 'data/root.reducers';
import * as globalSelectors from 'global/global-selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as collectionFunctionSelectors from 'data/collectionFunction/collectionFunction.selectors';
import * as collectionFunctionActions from 'data/collectionFunction/collectionFunction.actions';
import * as collectionFunctionGroupSelectors from 'data/collectionFunctionGroup/collectionFunctionGroup.selectors';
import * as userSelectors from 'users/users-selectors';
import * as contactSelectors from 'data/contact/contact.selectors';
import * as templateSelectors from 'data/template/template.selectors';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import * as cfFtpInfoSelectors from 'data/cfFtpInfo/cfFtpInfo.selectors';
import * as cfFtpInfoActions from 'data/cfFtpInfo/cfFtpInfo.actions';
import * as networkSelectors from 'data/network/network.selectors';
import * as securityInfoSelectors from 'data/securityInfo/securityInfo.selectors';
import * as fqdnGroupSelectors from 'data/fqdnGroup/fqdnGroup.selectors';
import * as nodeSelectors from 'data/nodes/nodes.selectors';
import * as locationSelectors from 'data/location/location.selectors';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { FormTemplate } from 'data/template/template.types';
import { adaptToForm } from 'data/collectionFunction/collectionFunction.adapter';
import { CollectionFunctionFormData, CollectionFunctionInterfaceFormData } from 'data/collectionFunction/collectionFunction.types';
import * as locationActions from 'data/location/location.actions';
import { FieldUpdateHandlerParam } from 'shared/form';
import { RetrievingDataProgress } from 'components';
import CollectionFunctionForm, { CollectionFunctionFormRef } from '../../components/CollectionFunctionForm';
import {
  adaptTemplateForInterfacesFieldUpdate,
  adaptTemplateInterfaceFields,
  adaptTemplateInterfaceTransportType,
  adaptTemplateOptions,
  adaptTemplateTimezoneOptions,
  adaptTemplateVisibilityMode,
  adaptTemplate5GFields,
  adaptTemplateInterfaceMdfNodeName,
  adaptTemplateStoreAndForward,
  adaptTemplateFtpInfoId,
  adaptTemplateSecinfoidFields,
  adaptLocationField,
  adaptTemplatesBasedOnAppSettings,
} from '../../utils/collectionFunctionTemplate.adapter';
import getCollectionFunctionDataToSubmit from '../../utils/getCollectionFunctionDataToSubmit';
import { createCollectionFunctionBasedOnTemplate } from '../../utils/collectionFunctionTemplate.factory';
import { handleInterfacesFieldUpdate } from '../../utils/handleInterfacesFieldUpdate';
import { handleInterfacesMdfNodeNameFieldUpdate } from '../../utils/handleInterfacesMdfNodeNameFieldUpdate';
import FieldCurrentState from '../../../../components/FieldCurrentState';
import { useFieldCreateNew } from '../../hooks/useFieldCreateNew';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  deliveryFunctionId: globalSelectors.getSelectedDeliveryFunctionId(state),
  selectedCollectionFunction: collectionFunctionSelectors.getSelectedCollectionFunction(state),
  collectionFunction: collectionFunctionSelectors.getCollectionFunction(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  timezones: userSelectors.getTimezones(state),
  contacts: contactSelectors.getContacts(state),
  cfFtpInfos: cfFtpInfoSelectors.getCfFtpInfoList(state),
  collectionFunctionGroupList: collectionFunctionGroupSelectors.getCollectionFunctionGroupList(state),
  collectionFunctionTemplates: templateSelectors.getEnabledCollectionFunctionFormTemplatesByKey(state),
  enabledCollectionFunctions: supportedFeaturesSelectors.getEnabledCollectionFunctions(state),
  networks: networkSelectors.getNetworks(state),
  securityInfos: securityInfoSelectors.getSecurityInfos(state),
  fqdnGroups: fqdnGroupSelectors.getFqdnGroups(state),
  is5GCVcpEnabled: supportedFeaturesSelectors.is5gcVcpEnabled(state),
  xcipioNodes: nodeSelectors.getNodes(state),
  isCollectionFunctionGroupLoading: collectionFunctionGroupSelectors.isCollectionFunctionGroupLoading(state),
  countries: locationSelectors.getCountryList(state),
  stateNameByCountry: locationSelectors.getStateListByCountry(state),
  filteredCities: locationSelectors.getFilteredCityList(state),
  isLocationRequired: discoverySelectors.isLocationRequired(state),
  isT1678HITlsEnabled: discoverySelectors.isT1678HITlsEnabled(state),
  is33108HITlsEnabled: discoverySelectors.is33108HITlsEnabled(state),
});

const mapDispatch = {
  fetchCollectionFunctionWithViewOptions: collectionFunctionActions.fetchCollectionFunctionWithViewOptions,
  updateCollectionFunction: collectionFunctionActions.updateCollectionFunction,
  goToCollectionFunctionList: collectionFunctionActions.goToCollectionFunctionList,
  getCfFtpInfoList: cfFtpInfoActions.getCfFtpInfoList,
  fetchLocationByFilterWithViewCities: locationActions.fetchLocationByFilterWithViewCities,
  fetchLocationList: locationActions.fetchLocationList,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>
type Props = PropsFromRedux & PropsFromRouter;

export const CollectionFunctionEdit: FunctionComponent<Props> = ({
  deliveryFunctionId,
  selectedCollectionFunction,
  collectionFunction,
  history,
  isAuditEnabled,
  userId,
  userName,
  timezones,
  contacts,
  cfFtpInfos,
  collectionFunctionGroupList,
  collectionFunctionTemplates,
  enabledCollectionFunctions,
  networks,
  securityInfos,
  fqdnGroups,
  is5GCVcpEnabled,
  xcipioNodes,
  isCollectionFunctionGroupLoading,
  countries,
  stateNameByCountry,
  filteredCities,
  isLocationRequired,
  isT1678HITlsEnabled,
  is33108HITlsEnabled,
  fetchCollectionFunctionWithViewOptions,
  updateCollectionFunction,
  goToCollectionFunctionList,
  getCfFtpInfoList,
  fetchLocationByFilterWithViewCities,
  fetchLocationList,
}: Props) => {
  const [templates, setTemplates] = useState<{ [key: string]: FormTemplate }>({});
  const [template, setTemplate] = useState<FormTemplate>();
  const [data, setData] = useState<Partial<CollectionFunctionFormData>>({});
  const formRef = useRef<CollectionFunctionFormRef>(null);
  const [handleFieldCreateNew] = useFieldCreateNew(
    (param, formSection, tabId) => handleFieldUpdate(param, formSection, tabId),
  );
  const { t } = useTranslation();

  const collectionFunctionId: string | undefined = selectedCollectionFunction[0]?.id;

  const customFieldComponentMap = {
    collectionFunctionCurrentState: FieldCurrentState,
  };

  useEffect(() => {
    if (collectionFunctionId) {
      fetchCollectionFunctionWithViewOptions(deliveryFunctionId, collectionFunctionId);
    }
    getCfFtpInfoList(deliveryFunctionId);
    fetchLocationList();
  }, [
    collectionFunctionId,
    deliveryFunctionId,
    fetchCollectionFunctionWithViewOptions,
    getCfFtpInfoList,
    fetchLocationList,
  ]);

  useEffect(() => {
    let availableTemplates = adaptTemplateOptions(
      collectionFunctionTemplates,
      timezones,
      contacts,
      collectionFunctionGroupList,
      cfFtpInfos,
      networks,
      securityInfos,
      fqdnGroups,
      enabledCollectionFunctions,
      xcipioNodes,
      countries,
    );

    availableTemplates = adaptTemplatesBasedOnAppSettings(
      availableTemplates,
      isLocationRequired,
      isT1678HITlsEnabled,
      is33108HITlsEnabled,
    )

    setTemplates(availableTemplates);
  }, [
    collectionFunctionTemplates,
    timezones,
    contacts,
    cfFtpInfos,
    collectionFunctionGroupList,
    enabledCollectionFunctions,
    networks,
    securityInfos,
    fqdnGroups,
    xcipioNodes,
    countries,
  ]);

  useEffect(() => {
    if (collectionFunction != null && templates != null) {
      let formData = data as CollectionFunctionFormData;
      if (formData.tag == null) {
        formData = adaptToForm(collectionFunction);
        setData(formData);
      }

      let templateData = adaptTemplateVisibilityMode(templates[formData.tag], 'edit');
      templateData = adaptTemplateTimezoneOptions(templateData, formData, timezones);
      templateData = adaptTemplateInterfaceFields(templateData, formData);
      templateData = adaptTemplate5GFields(templateData, is5GCVcpEnabled);
      templateData = adaptTemplateSecinfoidFields(templateData, isT1678HITlsEnabled, is33108HITlsEnabled);

      if (data) {
        const enable = data.ftpId !== '';
        templateData = adaptTemplateStoreAndForward(templateData, enable, formData.tag);
      }

      const { country, stateName } = formData;

      if (country) {
        const stateNames = stateNameByCountry[country]?.map(({ stateName: entry }) => entry) ?? [];
        templateData = adaptLocationField(templateData, stateNames, 'stateName');
      }

      if (country && stateName) {
        const { stateCode } = stateNameByCountry[country]?.find(({ stateName: entry }) => entry === stateName) ?? {};
        fetchLocationByFilterWithViewCities(`country eq '${country}' and stateCode eq '${stateCode}'`);
      }

      setTemplate(templateData);
    }
  }, [collectionFunction, templates, timezones, is5GCVcpEnabled]);

  useEffect(() => {
    if (filteredCities.length > 0 && data?.country && data?.stateName) {
      const templateData = adaptLocationField(template, filteredCities, 'city');
      setTemplate(templateData);
    }
  }, [filteredCities]);

  const handleClickSave = () => {
    if (!collectionFunctionId) {
      return;
    }

    const isValid = formRef?.current?.isValid(true, true) ?? true;
    if (!isValid) {
      return;
    }

    const formData = formRef?.current?.getValues();
    if (!formData) {
      return;
    }

    // update the "ownIPAddr" in hi2 interfaces to allow updating empty string to "ADDR_ANY".
    formData?.hi2Interfaces?.forEach((ele) => {
      if (ele.ownIPAddr === '' && formData.tag === 'CF-3GPP-33108') {
        ele.ownIPAddr = 'ANY_ADDR';
      } else if (ele.ownIPAddr === '' && formData.tag !== 'CF-3GPP-33108') {
        ele.ownIPAddr = 'ADDR_ANY';
      }
    });

    const collectionFunctionBody = getCollectionFunctionDataToSubmit(
      formData,
      collectionFunction,
      'UPDATE',
    );

    updateCollectionFunction(deliveryFunctionId, collectionFunctionId, collectionFunctionBody, history);
  };

  const handleCancel = () => {
    goToCollectionFunctionList(history);
  };

  const handleInterfaceAdd = (formSection: 'hi2Interfaces' | 'hi3Interfaces') => {
    const clonedData = cloneDeep(data);

    const newData = createCollectionFunctionBasedOnTemplate(template, true);
    const newInterfaceData = (newData[formSection] as CollectionFunctionInterfaceFormData[])[0];
    (clonedData[formSection] as CollectionFunctionInterfaceFormData[]).push(newInterfaceData);

    setData(clonedData);
  };

  const handleInterfaceRemove = (tabId: string | number, formSection: 'hi2Interfaces' | 'hi3Interfaces') => {
    const clonedData = cloneDeep(data);

    clonedData[formSection] = clonedData[formSection]?.filter(({ id }) => id !== tabId);

    setData(clonedData);
  };

  const handleFieldUpdate = (
    { name: fieldName, value }: FieldUpdateHandlerParam,
    formSection?: 'main' | 'facilityInformation' | 'hi2Interfaces' | 'hi3Interfaces',
    interfaceId?: string,
  ) => {
    const clonedData = cloneDeep(data);

    if (formSection === 'main' || formSection === 'facilityInformation') {
      if (fieldName === 'country') {
        const stateNames = stateNameByCountry[value]?.map(({ stateName }) => stateName) ?? [];

        let templateData = adaptLocationField(template, stateNames, 'stateName');
        templateData = adaptLocationField(templateData, [], 'city');

        setData({
          ...clonedData,
          [fieldName]: value,
          stateName: '',
          city: '',
        });
        setTemplate(templateData);

      } else if (fieldName === 'stateName') {
        const { country = '' } = clonedData ?? {};
        const { stateCode } = stateNameByCountry[country]?.find(({ stateName }) => stateName === value) ?? {};

        if (country && stateCode) {
          fetchLocationByFilterWithViewCities(`country eq '${country}' and stateCode eq '${stateCode}'`);
        }

        const templateData = adaptLocationField(template, [], 'city');

        setData({
          ...clonedData,
          [fieldName]: value,
          city: '',
        });
        setTemplate(templateData);

      } else if (fieldName === 'storeAndForward') {
        const adaptedTemplate = adaptTemplateFtpInfoId(template, fieldName, value);
        clonedData.ftpId = '';
        setData({
          ...clonedData,
          [fieldName]: value,
        });
        setTemplate(adaptedTemplate);
      } else if (fieldName === 'deliveryThru') {
        if (value === 'AF_DELIVERY') {
          clonedData.hi2Interfaces = [];
          clonedData.hi3Interfaces = [];
        } else {
          const newData = createCollectionFunctionBasedOnTemplate(template);
          clonedData.hi2Interfaces = newData.hi2Interfaces;
          clonedData.hi3Interfaces = newData.hi3Interfaces;
        }
      }
      setData({
        ...clonedData,
        [fieldName]: value,
      });

    } else if (formSection === 'hi2Interfaces' || formSection === 'hi3Interfaces') {
      if (fieldName === 'transportType' && clonedData[formSection]?.[0].transportType !== value) {
        setTemplate((prevTemplate) => {
          // Changing the collection function interface transport type should reset the interfaces
          const adaptedTemplate = adaptTemplateInterfaceTransportType(prevTemplate, formSection, value);
          const initialData = createCollectionFunctionBasedOnTemplate(adaptedTemplate, true);

          clonedData[formSection] = initialData[formSection] as CollectionFunctionInterfaceFormData[];

          setData(clonedData);
          return adaptedTemplate;
        });

      } else if (fieldName === 'mdf2NodeName' || fieldName === 'mdf3NodeName') {
        // Adapt the template so all new created interfaces have the same value
        const adaptedTemplate = adaptTemplateInterfaceMdfNodeName(template, formSection, fieldName, value);
        // Set the mdf node name to all interfaces
        clonedData[formSection] = handleInterfacesMdfNodeNameFieldUpdate(
          clonedData[formSection],
          fieldName,
          value,
        );

        setData(clonedData);
        setTemplate(adaptedTemplate);

      } else {
        clonedData[formSection] = handleInterfacesFieldUpdate(
          clonedData[formSection],
          interfaceId,
          fieldName,
          value,
        );

        setData(clonedData);
        setTemplate((prevTemplate) => adaptTemplateForInterfacesFieldUpdate(
          formSection,
          clonedData,
          prevTemplate,
          fieldName,
          value,
        ));
      }
    }
  };

  if (!template || !data) {
    return null;
  }

  return (
    <div className="app-container-content" data-testid="CollectionFunctionEdit">
      <CollectionFunctionForm
        ref={formRef}
        title={t('collectionFunction.edit.title')}
        isEditing
        data={data}
        template={template}
        customFieldComponentMap={customFieldComponentMap}
        onClickSave={handleClickSave}
        onClickCancelClose={handleCancel}
        onFieldUpdate={handleFieldUpdate}
        onFieldCreateNew={handleFieldCreateNew}
        onInterfaceAdd={handleInterfaceAdd}
        onInterfaceRemove={handleInterfaceRemove}
      />
      <RetrievingDataProgress isOpen={isCollectionFunctionGroupLoading} message={t('collectionFunction.savingLea')} />
    </div>
  );
};

export default withRouter(connector(CollectionFunctionEdit));
